import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LeafMark } from '../components/LeafMark';
import { pingQuery, type PingResponse } from '../lib/api';
import { useMe } from '../lib/auth';
import type { ChecklistAccount, ChecklistEntry, ChecklistResponse } from '../lib/checklist';
import { useChecklistToday } from '../lib/checklist';
import { formatRemaining, nextDailyResetUtc, nextWeeklyResetUtc } from '../lib/period';

interface OwnedEntry {
  entry: ChecklistEntry;
  ownerLabel: string;
}

function openEntriesFor(response: ChecklistResponse, cadence: ChecklistEntry['cadence']): OwnedEntry[] {
  const rows: OwnedEntry[] = [];
  for (const account of response.accounts) {
    for (const entry of account.tasks) {
      if (entry.cadence === cadence && !entry.done) {
        rows.push({ entry, ownerLabel: account.label });
      }
    }
    for (const character of account.characters) {
      for (const entry of character.tasks) {
        if (entry.cadence === cadence && !entry.done) {
          rows.push({ entry, ownerLabel: character.name });
        }
      }
    }
  }
  return rows;
}

/** Whichever character (not account) owns the most still-open dailies — the one the briefing names. */
function spotlightOwner(openDailies: OwnedEntry[], accounts: ChecklistAccount[]): string | null {
  const characterNames = new Set(accounts.flatMap((a) => a.characters.map((c) => c.name)));
  const counts = new Map<string, number>();
  for (const { ownerLabel } of openDailies) {
    if (!characterNames.has(ownerLabel)) {
      continue;
    }
    counts.set(ownerLabel, (counts.get(ownerLabel) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [name, count] of counts) {
    if (count > bestCount) {
      best = name;
      bestCount = count;
    }
  }
  return best;
}

/**
 * The dashboard is a briefing, not a stat wall (docs/DESIGN.md voice rules): "Reset in 6h 12m — 3
 * dailies left on Mira" beats a wall of vanity numbers. Built entirely from `useChecklistToday`,
 * the same query the checklist page already fetches.
 */
function useLiveNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function Briefing() {
  const now = useLiveNow();
  const todayQuery = useChecklistToday();

  if (todayQuery.isPending) {
    return <p className="text-sm text-text-muted">Reading today's log…</p>;
  }
  if (todayQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load today's briefing. Please retry in a moment.
      </p>
    );
  }

  const response = todayQuery.data;
  const accounts = response.accounts;

  if (accounts.length === 0) {
    return (
      <div className="flex items-center gap-3">
        <LeafMark className="text-text-faint" size={26} />
        <p className="text-sm text-text-muted">
          No accounts yet — add your main under{' '}
          <Link
            to="/characters"
            className="inline-flex items-center rounded-full bg-accent-dim px-2.5 py-0.5 text-xs font-semibold text-accent-strong transition-colors hover:bg-accent hover:text-accent-contrast"
          >
            Characters
          </Link>{' '}
          and let's get tracking.
        </p>
      </div>
    );
  }

  const openDailies = openEntriesFor(response, 'DAILY');
  const openWeeklies = openEntriesFor(response, 'WEEKLY');
  const dailyRemaining = formatRemaining(nextDailyResetUtc(now).getTime() - now.getTime());
  const weeklyRemaining = formatRemaining(nextWeeklyResetUtc(now).getTime() - now.getTime());

  if (openDailies.length === 0 && openWeeklies.length === 0) {
    return (
      <div className="flex items-center gap-3">
        <LeafMark className="text-crystal" size={26} />
        <div>
          <p className="font-display text-base font-semibold text-text">All wrapped up for today.</p>
          <p className="text-sm text-text-muted">Dailies reset in {dailyRemaining} — nothing else owed.</p>
        </div>
      </div>
    );
  }

  const spotlight = spotlightOwner(openDailies, accounts);
  const dailyLine =
    openDailies.length === 0
      ? "Dailies are clear."
      : `${openDailies.length} dail${openDailies.length === 1 ? 'y' : 'ies'} left${spotlight ? ` on ${spotlight}` : ''}.`;

  return (
    <div>
      <p className="font-display text-lg font-semibold text-text">
        Reset in {dailyRemaining} — {dailyLine}
      </p>
      <p className="mt-1 text-sm text-text-muted">
        {openWeeklies.length === 0
          ? 'Weeklies are clear.'
          : `${openWeeklies.length} weekly item${openWeeklies.length === 1 ? '' : 's'} still open, ${weeklyRemaining} until the Thursday reset.`}
      </p>
    </div>
  );
}

function ApiStatus() {
  const { data, isPending, isError } = useQuery<PingResponse>(pingQuery);

  if (isPending) {
    return <span className="text-text-muted">checking…</span>;
  }
  if (isError) {
    return <span className="text-danger">unreachable</span>;
  }
  return (
    <span className="text-success">
      {data.status} <span className="text-text-faint">(v{data.version})</span>
    </span>
  );
}

export function Dashboard() {
  const { data: me } = useMe();

  return (
    <div className="stagger-children space-y-6">
      <section className="rounded-xl border border-border bg-bg-elevated p-6">
        <h1 className="sr-only">Dashboard</h1>
        <Briefing />
      </section>

      {me?.role === 'ADMIN' && (
        <section className="rounded-xl border border-border bg-bg-elevated p-6">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">
            API status
          </h2>
          <p className="mt-2 text-sm tabular-nums" data-testid="api-status">
            /api/ping → <ApiStatus />
          </p>
        </section>
      )}
    </div>
  );
}
